/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Sunday April 14 2024
 **/

import { GestureRecognizer, FilesetResolver } from "@mediapipe/tasks-vision";
import { GestureRecognizerResult } from "@mediapipe/tasks-vision";
import { useRef, useState } from "react";
// import { Properties } from "@/frameworks/libraries/properties";

const LOGTAG = "#useHandGesture#";

export type Gesture = {
	categoryName: string;
	categoryScore: string;
	score: number;
	displayName: string;
};

const useHandGesture = (resultCallback?: (gesture: Gesture) => void) => {
	const videoElement = useRef<HTMLVideoElement | null>(null);
	const gestureRecognizer = useRef<GestureRecognizer | null>(null);
	const lastVideoTime = useRef<number>(-1);
	const [message, setMessage] = useState("");

	const create = async (element: HTMLVideoElement) => {
		if (gestureRecognizer.current) {
			destroy();
		}

		if (!gestureRecognizer.current) {
			videoElement.current = element;
			const vision = await FilesetResolver.forVisionTasks(
				`${location.origin /*Properties.property.webDomain*/}/storage/tasks-vision/wasm` //"https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm"
			);
			gestureRecognizer.current = await GestureRecognizer.createFromOptions(vision, {
				baseOptions: {
					modelAssetPath: `${location.origin /*Properties.property.webDomain*/}/storage/gesture_recognizer.task`,
					// "https://storage.googleapis.com/mediapipe-models/gesture_recognizer/gesture_recognizer/float16/1/gesture_recognizer.task",
					delegate: "GPU"
				},
				numHands: 2,
				runningMode: "VIDEO"
			});

			console.debug(`${LOGTAG} created`);
		}
	};
	const destroy = () => {
		gestureRecognizer.current?.close?.();
		gestureRecognizer.current = null;
		videoElement.current = null;

		console.debug(`${LOGTAG} destroyed`);
	};

	const detecting = async () => {
		if (!videoElement.current || !gestureRecognizer.current) {
			return;
		}

		const nowInMS = Date.now();
		let results: GestureRecognizerResult | undefined = undefined;

		if (videoElement.current.currentTime !== lastVideoTime.current) {
			results = gestureRecognizer.current.recognizeForVideo(videoElement.current, nowInMS);
			lastVideoTime.current = videoElement.current.currentTime;
		}
		if (results && results.gestures.length > 0) {
			const { categoryName, score } = results.gestures[0][0];
			const { displayName } = results.handedness[0][0];
			// @ts-ignore
			const categoryScore = parseFloat(score * 100).toFixed(2);

			setMessage(`${categoryName}|${categoryScore}|${displayName}`);
			resultCallback?.({
				categoryName,
				categoryScore,
				score,
				displayName
			});
		}

		window.requestAnimationFrame(() => {
			detecting();
		});
	};

	return {
		create,
		destroy,
		detecting,
		videoElement: videoElement.current,
		message,
		get isCreated() {
			return gestureRecognizer.current;
		}
	};
};
export default useHandGesture;
